import { isEqual } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';

import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';

import { hasPermission } from 'src/utils/has-permissions';

import { useAuthContext } from 'src/auth/hooks';
import { AuthPermissions } from 'src/auth/permissions';
import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import {
  deleteStationeryStockCount,
  getStationeryStockCounts,
  removeStationeryStockCount,
} from 'src/redux/slices/stationery-stock-count';

import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import TableErrorData from 'src/components/table/table-error-data';
import {
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSkeleton,
  useTable,
} from 'src/components/table';

import {
  IStationeryStockCountListItem,
  IStationeryStockCountTableFilters,
  IStationeryStockCountTableFilterValue,
  IStationeryStockCountTableSort,
} from 'src/types/stationery-stock-count';

import StationeryStockCountTableRow from '../stationery-stock-count-table-row';
import StationeryStockCountTableToolbar from '../stationery-stock-count-table-toolbar';

const TABLE_HEAD = [
  { id: 'stockCountCode', label: 'Stock Count #', align: 'left' },
  { id: 'countDate', label: 'Count Date', align: 'left' },
  { id: 'remarks', label: 'Remarks', align: 'left' },
  { id: '' },
];

const defaultFilters: IStationeryStockCountTableFilters = {
  generalSearch: '',
  startDate: null,
  endDate: null,
};

// Sorted by createdAt (not updatedAt) so the top row on an unfiltered first
// page always matches the backend's "most recently created" rule — that's
// the only row delete is enabled for.
const defaultSortBy: IStationeryStockCountTableSort = {
  sortBy: 'createdAt',
  order: 'desc',
};

export default function StationeryStockCountListView() {
  const { status, stockCounts, error } = useAppSelector(
    (state) => state.stationeryStockCount
  );

  const { user: authUser } = useAuthContext();
  const table = useTable({
    defaultRowsPerPage: 25,
  });

  const router = useRouter();

  const dispatch = useAppDispatcher();

  const { page, rowsPerPage } = table;

  const settings = useSettingsContext();

  const denseHeight = table.dense ? 60 : 80;

  const [filters, setFilters] = useState(defaultFilters);

  const [sort, setSort] = useState(defaultSortBy);

  const canReset = !isEqual(defaultFilters, filters);

  const canDetermineLastStockCount =
    page === 0 &&
    sort.sortBy === 'createdAt' &&
    sort.order === 'desc' &&
    !filters.generalSearch &&
    !filters.startDate &&
    !filters.endDate;

  const handleFilters = useCallback(
    (name: string, value: IStationeryStockCountTableFilterValue) => {
      table.onResetPage();

      setFilters((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    },
    [table]
  );

  const handleResetFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  const handleEditRow = useCallback(
    (id: string) => {
      router.push(paths.dashboard.stationeryStockCount.edit(id));
    },
    [router]
  );

  const fetchPaginatedStockCounts = useCallback(() => {
    dispatch(
      getStationeryStockCounts({
        page,
        limit: rowsPerPage,
        generalSearch: filters.generalSearch,
        startDate: filters.startDate,
        endDate: filters.endDate,
        sort: sort.order,
        sortBy: sort.sortBy,
      })
    );
  }, [dispatch, page, rowsPerPage, sort.order, sort.sortBy, filters]);

  const handleDeleteRow = useCallback(
    async (item: IStationeryStockCountListItem, index: number) => {
      dispatch(removeStationeryStockCount({ stockCountId: item._id }));

      try {
        await dispatch(deleteStationeryStockCount({ item, index })).unwrap();

        fetchPaginatedStockCounts();
        enqueueSnackbar('Stock count deleted');
      } catch (e) {
        if (typeof e === 'string') {
          enqueueSnackbar(e, {
            variant: 'error',
          });
        } else {
          enqueueSnackbar(e?.message, {
            variant: 'error',
          });
        }
      }
    },
    [dispatch, fetchPaginatedStockCounts]
  );

  useEffect(() => {
    fetchPaginatedStockCounts();
  }, [fetchPaginatedStockCounts]);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Stationery Stock Counts"
        links={[]}
        action={
          <>
            {hasPermission(
              authUser,
              'any',
              AuthPermissions.ADMIN_PERMISSION,
              AuthPermissions.SUPER_ADMIN_PERMISSION
            ) && (
              <Button
                component={RouterLink}
                href={paths.dashboard.stationeryStockCount.new}
                variant="contained"
                startIcon={<Iconify icon="mingcute:add-line" />}
              >
                New Stock Count
              </Button>
            )}
          </>
        }
        sx={{
          mb: { xs: 3 },
        }}
      />

      <Card>
        <StationeryStockCountTableToolbar
          filters={filters}
          onFilters={handleFilters}
          handleResetFilters={handleResetFilters}
          canReset={canReset}
        />

        <TableContainer sx={{ position: 'relative', overflow: 'hidden', height: '450px' }}>
          <Scrollbar>
            <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 760 }}>
              <TableHeadCustom
                order={sort.order}
                orderBy={sort.sortBy}
                headLabel={TABLE_HEAD}
                rowCount={stockCounts.items.length}
                numSelected={table.selected.length}
                onSort={(id) => {
                  if (id === sort.sortBy) {
                    setSort((prev) => ({
                      ...prev,
                      order: prev.order === 'asc' ? 'desc' : 'asc',
                    }));
                  } else {
                    setSort({
                      sortBy: id,
                      order: 'asc',
                    });
                  }
                }}
              />

              <TableBody>
                {status === 'loading' ? (
                  [...Array(table.rowsPerPage)].map((i, index) => (
                    <TableSkeleton key={index} sx={{ height: denseHeight }} />
                  ))
                ) : (
                  <>
                    {stockCounts?.items.map(
                      (row: IStationeryStockCountListItem, index: number) => (
                        <StationeryStockCountTableRow
                          key={row._id}
                          row={row}
                          isEditable={canDetermineLastStockCount && index === 0}
                          onDeleteRow={() => handleDeleteRow(row, index)}
                          onEditRow={() => handleEditRow(row._id)}
                        />
                      )
                    )}
                  </>
                )}

                {status !== 'failure' && stockCounts.count === 0 && (
                  <TableNoData
                    message="No stock counts found"
                    notFound={stockCounts.count === 0}
                  />
                )}

                {status === 'failure' && <TableErrorData notFound error={error} />}
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>

        <TablePaginationCustom
          count={stockCounts.count}
          page={table.page}
          rowsPerPage={table.rowsPerPage}
          onPageChange={table.onChangePage}
          onRowsPerPageChange={table.onChangeRowsPerPage}
          dense={table.dense}
          onChangeDense={table.onChangeDense}
        />
      </Card>
    </Container>
  );
}
